import { useCallback } from "react";

import { PollingIntervalGroups } from "apollo/constants";
import { EmptystateStacksColored } from "components/icons/generated";
import TablePagination from "components/Table/Pagination";
import usePagination from "components/Table/Pagination/usePagination";
import Button from "ds/components/Button";
import EmptyState from "ds/components/EmptyState";
import TableSimple from "ds/components/TableSimple";
import useErrorHandle from "hooks/useErrorHandle";
import useTitle from "hooks/useTitle";
import useTypedContext from "hooks/useTypedContext";
import { showOpenTofuMigrationClearQueueConfirmationModal } from "shared/Stack/OpenTofuMigration/ClearQueueConfirmationModal";
import useOpenTofuMigrationCounts from "shared/Stack/OpenTofuMigration/useCounts";
import { AccountContext } from "views/AccountWrapper";

import OpenTofuMigrationViewHeader from "../ViewHeader";
import {
  CLEAR_QUEUE_REFETCH_QUERIES,
  MIGRATION_QUEUE_FILTERS_ORDER_SETTINGS_KEY,
  MIGRATION_QUEUE_LIMIT_STORAGE_KEY,
} from "./constants";
import { OPEN_TOFU_MIGRATION_INITIAL_LIMIT, OPEN_TOFU_MIGRATION_TITLE } from "../constants";
import useOpenTofuMigrationSearchQueueEntries from "./useSearchEntries";
import useOpenTofuMigrationSearchQueueSuggestions from "./useSearchSuggestions";
import OpenTofuMigrationQueueFiltersLayout from "./FiltersLayout";
import { MIGRATION_QUEUE_TABLE_CELLS } from "./Table/Cells";
import { MIGRATION_QUEUE_SORT_CONFIG } from "./Table/constants";
import useOpenTofuMigrationQueueTableStore from "./Table/useTableStore";

const disableStickyRight = () => false;

const OpenTofuMigrationQueue = () => {
  const { accountName } = useTypedContext(AccountContext);

  useTitle(`${OPEN_TOFU_MIGRATION_TITLE} · ${accountName}`);

  const { pendingEntries } = useOpenTofuMigrationCounts(PollingIntervalGroups.Lists);

  const {
    entities,
    error,
    loadingContent,
    loadPage,
    endCursor,
    startCursor,
    isPageEmpty,
    hasNoFilteringResults,
    paginationResetKey,
  } = useOpenTofuMigrationSearchQueueEntries();

  const {
    filters,
    filtersMap,
    sortOptions,
    loading: filtersLoading,
    filteredCount,
  } = useOpenTofuMigrationSearchQueueSuggestions();

  const {
    rowsPerPage,
    setRowsPerPage,
    from,
    to,
    lastPage,
    page,
    goToNextPage,
    goToPrevPage,
    goToFirstPage,
    goToLastPage,
  } = usePagination({
    fetchItems: loadPage,
    endCursor,
    startCursor,
    allItemsCount: filteredCount,
    visibleItemsCount: entities.length,
    initialLimit: OPEN_TOFU_MIGRATION_INITIAL_LIMIT,
    storageKey: MIGRATION_QUEUE_LIMIT_STORAGE_KEY,
    resetKey: paginationResetKey,
    loading: loadingContent,
  });

  const handleClearQueue = useCallback(
    () =>
      showOpenTofuMigrationClearQueueConfirmationModal({
        refetchQueries: CLEAR_QUEUE_REFETCH_QUERIES,
        onCleared: goToFirstPage,
      }),
    [goToFirstPage]
  );

  const ErrorContent = useErrorHandle(error);

  if (ErrorContent) {
    return ErrorContent;
  }

  return (
    <>
      <OpenTofuMigrationViewHeader>
        <Button variant="secondary" onPress={handleClearQueue} disabled={pendingEntries === 0}>
          Clear pending entries
        </Button>
      </OpenTofuMigrationViewHeader>

      <OpenTofuMigrationQueueFiltersLayout
        isPageEmpty={isPageEmpty}
        filters={filters}
        filtersMap={filtersMap}
        sortOptions={sortOptions}
        filtersLoading={filtersLoading}
      >
        {isPageEmpty && (
          <EmptyState
            title="Nothing queued yet"
            icon={EmptystateStacksColored}
            caption="Stacks queued for migration will show up here."
          />
        )}
        {hasNoFilteringResults && (
          <EmptyState
            title="No results"
            icon={EmptystateStacksColored}
            caption="Try different filters."
            announce
          />
        )}
        {(!!entities.length || loadingContent) && (
          <>
            <TableSimple
              data={entities}
              loading={loadingContent}
              store={useOpenTofuMigrationQueueTableStore}
              ariaLabel="OpenTofu migration queue"
              cellPerColumn={MIGRATION_QUEUE_TABLE_CELLS}
              filtersStorageKey={MIGRATION_QUEUE_FILTERS_ORDER_SETTINGS_KEY}
              sortConfig={MIGRATION_QUEUE_SORT_CONFIG}
              hasPagination
              hideSettings
              stickyRight={disableStickyRight}
            />
            <TablePagination
              from={from}
              to={to}
              loading={loadingContent}
              goToNextPage={goToNextPage}
              setRowsPerPage={setRowsPerPage}
              goToPrevPage={goToPrevPage}
              goToFirstPage={goToFirstPage}
              goToLastPage={goToLastPage}
              rowsPerPage={rowsPerPage}
              itemsCount={filteredCount}
              lastPage={lastPage}
              page={page}
            />
          </>
        )}
      </OpenTofuMigrationQueueFiltersLayout>
    </>
  );
};

export default OpenTofuMigrationQueue;
